
import { useState } from "react";
import { toast } from "sonner";
import { verifyGiftCard } from "@/lib/supabase";
import { useTranslation } from "react-i18next";

export const useGiftCardVerification = () => {
  const [giftCardVerified, setGiftCardVerified] = useState(false);
  const [verifiedGiftCardId, setVerifiedGiftCardId] = useState<string | null>(null);
  const [isVerifyingCard, setIsVerifyingCard] = useState(false);
  const { t } = useTranslation();
  
  const handleVerifyGiftCard = async (giftCardCode: string) => {
    if (!giftCardCode || giftCardCode.trim() === "") {
      toast.error(t("booking.giftCard.emptyCodeError"));
      return;
    }
    
    setIsVerifyingCard(true);
    console.log("Verifying gift card:", giftCardCode);
    
    try {
      const result = await verifyGiftCard(giftCardCode);
      
      if (result.success && result.data) {
        setGiftCardVerified(true);
        setVerifiedGiftCardId(result.data.id);
        console.log("Gift card verified successfully:", { 
          id: result.data.id, 
          verified: true, 
          remainingClasses: result.data.remainingClasses 
        });
        
        toast.success(t("booking.giftCard.verifiedTitle"), {
          description: t("booking.giftCard.verifiedDescription", { count: result.data.remainingClasses }),
        });
      } else {
        setGiftCardVerified(false);
        setVerifiedGiftCardId(null);
        console.log("Gift card verification failed:", result.error);
        
        // Handle the specific database error message and convert to user-friendly error
        const errorMessage = result.error || "";
        if (errorMessage.includes("JSON object requested, multiple (or no) rows returned")) {
          toast.error(t("booking.giftCard.invalidCard"), {
            description: t("booking.giftCard.cardNotFound"),
          });
        } else {
          toast.error(t("booking.giftCard.invalidCard"), {
            description: result.error || t("booking.giftCard.genericError"),
          });
        }
      }
    } catch (error: any) {
      console.error("Error verifying gift card:", error);
      setGiftCardVerified(false);
      setVerifiedGiftCardId(null);
      
      // Handle the specific error message
      const errorMessage = error?.message || "";
      if (errorMessage.includes("JSON object requested, multiple (or no) rows returned")) {
        toast.error(t("booking.giftCard.invalidCard"), {
          description: t("booking.giftCard.cardNotFound"),
        });
      } else {
        toast.error(t("booking.giftCard.verificationFailed"), {
          description: t("booking.giftCard.tryAgain"),
        });
      }
    } finally {
      setIsVerifyingCard(false);
    }
  };
  
  const resetGiftCardVerification = () => {
    console.log("Resetting gift card verification state");
    setGiftCardVerified(false);
    setVerifiedGiftCardId(null);
  };
  
  return {
    giftCardVerified,
    verifiedGiftCardId,
    isVerifyingCard,
    handleVerifyGiftCard,
    resetGiftCardVerification
  };
};
